import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { PageModel } from 'app/models/page-model';
import { PendingEventModel } from 'app/models/pending-event-model';
import { AppService } from 'app/services/app/app.service';
import { RestService } from 'app/services/rest/rest.service';

@Component({
  selector: 'app-admin-pending-event-page',
  templateUrl: './admin-pending-event.page.html',
  styleUrls: ['./admin-pending-event.page.scss']
})
export class PendingEventPage implements OnInit {
  dataSource = new MatTableDataSource<PendingEventModel>([]);
  displayedColumns: string[] = ['status', 'eventName', 'aggregate', 'payload', 'retryCount', 'lastSentAt', 'createdAt'];
  loading = false;
  pendingEvents: PageModel<PendingEventModel> = {
    content: [],
    pageNumber: 0,
    pageSize: 20,
    totalPages: 0,
    totalElements: 0,
    first: true,
    last: true
  };
  totalElements = 0;
  pageSize = 20;
  pageIndex = 0;
  pageSizeOptions: number[] = [10, 20, 25, 50];

  constructor(
    private restService: RestService,
    public appService: AppService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadPendingEvents();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadPendingEvents();
  }

  loadPendingEvents(page: number = this.pageIndex, size: number = this.pageSize): void {
    this.loading = true;
    this.restService.getPendingEvents(page, size).subscribe({
      next: (result) => {
        this.pendingEvents = result;
        this.dataSource.data = this.extractContent(result);
        this.totalElements = result?.totalElements ?? this.dataSource.data.length;
        this.pageSize = result?.pageSize ?? size;
        this.pageIndex = result?.pageNumber ?? page;
        this.loading = false;
      },
      error: () => {
        this.pendingEvents = {
          content: [],
          pageNumber: 0,
          pageSize: this.pageSize,
          totalPages: 0,
          totalElements: 0,
          first: true,
          last: true
        };
        this.dataSource.data = [];
        this.totalElements = 0;
        this.loading = false;
      }
    });
  }

  openDetails(row: PendingEventModel): void {
    this.restService.getPendingEvent(row.uuid).subscribe((pendingEvent: PendingEventModel) => {
      this.dialog.open(AdminPendingEventDetails, {
        panelClass: 'responsive-dialog',
        maxWidth: '95vw',
        data: { pendingEvent }
      });
    });
  }

  totalItems(): number {
    return this.totalElements;
  }

  statusClass(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'ACKED':
      case 'SENT':
      case 'SUCCESS':
        return 'pe-chip pe-chip--success';
      case 'FAILED':
      case 'ERROR':
        return 'pe-chip pe-chip--danger';
      case 'PENDING':
      case 'RETRY':
        return 'pe-chip pe-chip--warning';
      default:
        return 'pe-chip pe-chip--neutral';
    }
  }

  formatEnumLabel(value?: string): string {
    if (!value) {
      return '-';
    }

    return value
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  aggregateLabel(row: PendingEventModel): string {
    return `${this.valueOrDash(row.eventAggregateType)} · ${this.valueOrDash(row.eventAggregateId)}`;
  }

  payloadPreview(payload?: string): string {
    if (!payload) {
      return 'Sin payload';
    }

    return payload.length > 88 ? `${payload.slice(0, 88)}...` : payload;
  }

  formatDateTime(value?: string | number): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString('es-ES');
  }

  valueOrDash(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    return String(value);
  }

  private extractContent(result: any): PendingEventModel[] {
    if (Array.isArray(result)) {
      return result;
    }

    if (Array.isArray(result?.content)) {
      return result.content;
    }

    return [];
  }
}

@Component({
  selector: 'app-admin-pending-event-details',
  templateUrl: './admin-pending-event-details.component.html',
  styleUrls: ['./admin-pending-event.page.scss']
})
export class AdminPendingEventDetails {
  pendingEvent: PendingEventModel;

  constructor(
    public dialogRef: MatDialogRef<AdminPendingEventDetails>,
    @Inject(MAT_DIALOG_DATA) public data: { pendingEvent: PendingEventModel },
    public appService: AppService
  ) {
    this.pendingEvent = data?.pendingEvent;
  }

  close(): void {
    this.dialogRef.close();
  }

  statusClass(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'ACKED':
      case 'SENT':
      case 'SUCCESS':
        return 'pe-chip pe-chip--success';
      case 'FAILED':
      case 'ERROR':
        return 'pe-chip pe-chip--danger';
      case 'PENDING':
      case 'RETRY':
        return 'pe-chip pe-chip--warning';
      default:
        return 'pe-chip pe-chip--neutral';
    }
  }

  formatEnumLabel(value?: string): string {
    if (!value) {
      return '-';
    }

    return value
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  formatDateTime(value?: string | number): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString('es-ES');
  }

  valueOrDash(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    return String(value);
  }

  formatJson(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return 'Sin payload';
    }

    if (typeof value === 'string') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    }

    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
}
